import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Solution } from '@aveva/connect-web-core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { CONFIGURATION } from 'src/configuration';

@Injectable({
  providedIn: 'root'
})
export class SolutionsService {
  private http = inject(HttpClient);
  private configuration = inject(CONFIGURATION);

  private solutionsCache = new Map<string, Solution>();
  private cacheTimestamp: number | null = null;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  querySolutions(solutionIds: string): Observable<Solution[]> {
    if (this.isCacheValid()) {
      const ids = solutionIds.split(',');
      const cachedSolutions = ids.map(id => this.solutionsCache.get(id)).filter(Boolean) as Solution[];
      if (cachedSolutions.length === ids.length) {
        return of(cachedSolutions);
      }
    }

    const params = new HttpParams().set('ids', solutionIds);
    return this.http.get<Solution[]>(`${this.configuration.apis.scm}/solutions/`, { params: params })
      .pipe(
        map((data: any) => data._embedded['doc:solutions']), //eslint-disable-line  @typescript-eslint/no-explicit-any
        tap(solutions => {
          this.cacheTimestamp = Date.now();
          solutions.forEach(solution => this.solutionsCache.set(solution.id, solution));
        }),
        shareReplay(1),
        catchError(this.handleError)
      );
  }

  getSolution(solutionId: string): Observable<Solution> {
    if (this.isCacheValid() && this.solutionsCache.has(solutionId)) {
      return of(this.solutionsCache.get(solutionId)!);
    }

    return this.http.get<Solution>(`${this.configuration.apis.scm}/solutions/${solutionId}`)
      .pipe(
        tap(solution => {
          this.cacheTimestamp = Date.now();
          this.solutionsCache.set(solution.id, solution);
        }),
        catchError(this.handleError)
      );
  }

  private isCacheValid(): boolean {
    return this.cacheTimestamp !== null && (Date.now() - this.cacheTimestamp) < this.CACHE_TTL;
  }

  handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      return throwError(() => new Error(`Error: ${error.message}`));
    } else {
      // Server-side errors
      return throwError(() => new Error(`Error: Backend returned code ${error.status}: ${error.message}`));
    }
  }
}
